import React, { useEffect, useState, useMemo, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import axios from 'axios';
import { API_BASE, getAuthHeaders } from '../utils/api';
import { useUsersStore } from '../store/useUsersStore';
import { formatPakistanDate } from '../utils/dateUtils';

const MyBidsPreview = () => {
  const navigate = useNavigate();
  const [bids, setBids] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  
  const subUsers = useUsersStore((s) => s.users || []);
 const parentUid = useUsersStore((s) => s.parentUid); 
 

 const userMapping = useMemo(() => {
    return (Array.isArray(subUsers) ? subUsers : []).reduce((acc, u) => {
      const id = String(u.user_bid_id || u.bidder_id || u.user_bid || '');
      if (id) acc[id] = u.sub_username || u.name || acc[id] || `sub_${(u.document_id || '').slice(0, 6) || id}`;
      return acc;
    }, {});
  }, [subUsers]);

  const badgeClass = {
    bidder: 'bg-blue-100 text-blue-800 border-blue-300',
    period: 'bg-orange-100 text-orange-800 border-orange-300',
    bidType: 'bg-purple-100 text-purple-800 border-purple-300',
    projectType: 'bg-indigo-100 text-indigo-800 border-indigo-300',
    budget: {
      low: 'bg-green-100 text-green-800 border-green-300',
      medium: 'bg-yellow-100 text-yellow-800 border-yellow-300',
      high: 'bg-red-100 text-red-800 border-red-300',
    },
  };

  const getBudgetBadgeClass = (min, max) => {
    const amount = max || min || 0;
    if (amount <= 50) return badgeClass.budget.low;
    if (amount <= 500) return badgeClass.budget.medium;
    return badgeClass.budget.high;
  };

  const getBidAmountBadgeClass = (amount) => {
    if (amount == null) return 'bg-gray-100 text-gray-800 border-gray-300';
    if (amount <= 50) return 'bg-green-100 text-green-800 border-green-300';
    if (amount > 100) return 'bg-yellow-100 text-yellow-800 border-yellow-300';
    return 'bg-red-100 text-red-800 border-yellow-300';
  };

  const getBidderName = (id) => {
    if (userMapping[String(id)]) return userMapping[String(id)];
    const found = subUsers.find((u) => String(u.user_bid_id || u.bidder_id || '') === String(id));
    if (found) return found.sub_username || found.name || String(id);
    return 'No data found';
  };

  const getBidDate = (bid) => {
    try {
      return formatPakistanDate(bid.date || bid.createdAt || bid.created_at) || '';
    } catch {
      return new Date(bid.date || bid.createdAt || Date.now()).toLocaleString();
    }
  };

  const loadRecentBids = useCallback(async () => {
    try {
      setLoading(true);
      setError(null);
     // include parent_id query param when available so API returns bids for the parent user
      const base = `${(API_BASE || '').replace(/\/$/, '')}/bids?page=1&offset=4`;
      const url = parentUid ? `${base}&parent_id=${encodeURIComponent(parentUid)}` : base;
      const headers = getAuthHeaders();
      const res = await axios.get(url, { headers, validateStatus: () => true });
      
      if (!(res.status >= 200 && res.status < 300)) {
        throw new Error(res?.data?.message || `Failed to load bids: ${res.status}`);
      }
      
      const bidsData = Array.isArray(res.data?.data) ? res.data.data : [];
      setBids(bidsData.slice(0, 4));
    } catch (err) {
      console.error('Failed to load recent bids:', err);
      setError(err?.message || 'Failed to load bids');
      setBids([]);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    loadRecentBids();
  }, [loadRecentBids]);

  if (loading) {
    return (
      <div className="py-4 ">
        <div className="flex items-center justify-between mb-6">
          <h2 className="text-2xl font-bold text-gray-900">My Bids</h2>
          <button className="text-sm font-medium text-primary-blue">See all</button>
        </div>
        <div className="flex items-center justify-center py-12">
          <div className="text-gray-500">Loading recent bids...</div>
        </div>
      </div>
    );
  }

  return (
    <div className="py-6">
      {/* Header outside the box */}
      <div className="flex items-center justify-between mb-6">
        <h2 className="text-2xl font-bold text-gray-900">My Bids</h2>
        <button
          onClick={() => navigate('/dashboard/bids')}
          className="text-sm font-medium text-primary-blue hover:text-blue-700 transition-colors"
        >
          See all
        </button>
      </div>

      {/* Cards grid - 2 columns, 4 cards - using exact same UI as SuccessBidsPage */}
      {error ? (
        <div className="bg-white rounded shadow p-6 text-center text-red-600">
          {error}
        </div>
      ) : bids.length === 0 ? (
        <div className="bg-white rounded shadow p-12 text-center">
          <div className="text-gray-400 text-4xl mb-4">📋</div>
          <p className="text-gray-500">No saved bids found.</p>
        </div>
      ) : (
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
          {bids.map((bid) => {
            const key = bid.document_id || `${bid.project_id}_${bid.bidder_id}`;
            return (
              <div 
                key={key} 
                className="bg-white p-6 rounded-xl shadow flex flex-col cursor-pointer hover:shadow-lg border-2 border-light-blue"
                onClick={() => navigate('/bids')}
              >
                <div className="flex justify-between items-start">
                  <div>
                    <h3 className="text-lg font-semibold">{bid.projectTitle}</h3>
                    {/* <div className="text-xs text-gray-500 mt-1">{(bid.type || '').toUpperCase()}</div> */}
                  </div>
                  {/* <div className={`px-3 py-1.5 rounded-lg font-semibold border inline-block ${getBidAmountBadgeClass(bid.amount)}`}>
                    ${bid.amount ?? 'N/A'}
                  </div> */}
                </div>

                <p className="text-sm text-gray-600 mt-2 line-clamp-2">{bid.projectDescription}</p>

                {/* <div className="mt-2 text-sm text-gray-700">
                  <div className="text-xs text-gray-500">Proposal</div>
                  <div className="mt-1 text-sm text-gray-600 line-clamp-3">
                    {bid.description && bid.description.trim() !== '' ? bid.description : <span className="text-gray-400">No proposal provided</span>}
                  </div>
                </div> */}

                     
              <div className="mt-3 flex flex-wrap items-center gap-4 text-sm text-gray-600 opacity-40">
                
                {/* Project Type */}
                <div className="flex items-center gap-1">
                  <span className="text-xs">
                    Type: {(bid.type || 'N/A').toUpperCase()}
                  </span>
                </div>

                {/* Bid Type */}
                <div className="flex items-center gap-1">
                  <span className="text-xs">
                    Bid Type: {bid.bidder_type || 'N/A'}
                  </span>
                </div>

                {/* Bid Period */}
                <div className="flex items-center gap-1">
                  <span className="text-xs">
                    Bid Period: {bid.period ?? 'N/A'} days
                  </span>
                </div>

                {/* Budget */}
                <div className="flex items-center gap-1">
                  <span className="text-xs">
                    Budget: {bid.budget?.minimum != null ? `$${bid.budget.minimum}` : '-'}
                    {' '}–{' '}
                    {bid.budget?.maximum != null ? `$${bid.budget.maximum}` : '-'}
                  </span>
                </div>

              </div>

              {/* Icons row */}
              <div className="mt-3 flex flex-wrap items-center gap-4 text-sm opacity-40">

                {/* Bidder */}
                <div className="flex items-center gap-1.5">
                  <img src="/Icons/bid-person-icon.svg" alt="" className="w-4 h-4" />
                  <span className="text-gray-700">
                    {getBidderName(bid.bidder_id)}
                  </span>
                </div>

                {/* Bid Amount */}
                <div className="flex items-center gap-1.5">
                  <img src="/Icons/dollar-bid-icon.svg" alt="" className="w-4 h-4" />
                  <span className="text-gray-700">
                    ${bid.amount ?? 'N/A'}
                  </span>
                </div>

                {/* Date */}
                <div className="flex items-center gap-1.5">
                   <img src="/Icons/clander-icon.svg" alt="" className="w-4 h-4" />
                  <span className="text-gray-700">
                    {getBidDate(bid) ?? 'N/A'}
                  </span>
                </div>

              </div>


                <div className="mt-3 flex-1">
                  <div className="text-sm text-primary-black-shade-10 mb-2 font-semibold">Scores</div>
                  <div className="grid grid-cols-2 gap-2 text-sm bg-light-blue-shade-20 p-2 rounded-lg">
                    {bid.scores ? Object.entries(bid.scores).slice(0, 8).map(([k, v]) => {
                      const formatScore = (val) => {
                        if (val == null) return '-';
                        if (typeof val !== 'number') return String(val);
                        return Number.isInteger(val) ? String(val) : val.toFixed(2);
                      };
                      return (
                        <div key={k} className="flex justify-between bg-white px-2 py-1 rounded">
                          <span className="text-xs text-primary-black-shade-10 opacity-20">{k.replace(/_/g, ' ')}</span>
                          <span className="font-medium text-xs text-primary-black-shade-10 opacity-80 ">{formatScore(v)}</span>
                        </div>
                      );
                    }) : <div className="text-xs text-gray-400">No scores</div>}
                  </div>
                </div>

                <div className="mt-3 flex justify-center items-center gap-2 w-44 mx-auto">
                  <button 
                    onClick={(e) => {
                      e.stopPropagation();
                      window.open(`https://www.freelancer.com/projects/${bid.url}`, '_blank');
                    }} 
                    className="flex-1 px-2 py-1 bg-white text-primary-blue rounded hover:bg-primary-blue hover:text-white transition-colors border border-primary-blue "
                  >
                    View Project
                  </button>
                </div>
         </div>
            );
          })}
        </div>
      )}
    </div>
  );
};

export default MyBidsPreview;
